import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  DocumentationPageViewStats,
  listDocumentationLiveViews,
} from '../../services/documentationReportsApi';
import { useDocumentationShell } from './documentationShell';

export function DocumentationViewsPage() {
  const shell = useDocumentationShell();
  const pageSize = shell.kind === 'tenant' ? 10 : 25;
  const [items, setItems] = useState<DocumentationPageViewStats[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const result = await listDocumentationLiveViews({ page, pageSize: pageSize });
        if (cancelled) return;
        setItems(result.items ?? []);
        setTotalCount(result.totalCount ?? 0);
        setTotalViews(result.totalViewCount ?? 0);
        setPage(result.page ?? page);
      } catch {
        if (!cancelled) toast.error('Falha ao carregar visualizações.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <PageShell>
      <PageHeader
        title="Visualizações"
        description="Acessos à versão publicada das páginas no modo live (link público)."
      />

      {!loading ? (
        <p className="mb-4 text-sm text-secondary-600">
          Total de visualizações registradas: <strong>{totalViews}</strong>
        </p>
      ) : null}

      {loading ? (
        <LoadingSpinner label="Carregando visualizações..." />
      ) : items.length === 0 ? (
        <p className="text-secondary-600">Nenhuma visualização registrada ainda.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-secondary-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-secondary-50 text-secondary-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Documento</th>
                  <th className="px-4 py-3 font-medium">Página</th>
                  <th className="px-4 py-3 font-medium text-right">Visualizações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.pageId} className="border-t border-secondary-100">
                    <td className="px-4 py-3 text-secondary-700">{item.spaceName}</td>
                    <td className="px-4 py-3">
                      <Link
                        className="text-primary-600 hover:underline"
                        to={`${shell.basePath}/${item.spaceId}/pages/${item.pageId}`}
                      >
                        {item.pageTitle}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-secondary-800">{item.viewCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalCount > pageSize ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
